// §4.5 Planned orders (`/planning/orders`, P3-H-2, D-387, D-389, D-390): the
// facility's planned orders with every filter of §5 row 42, the All and Action
// messages tabs, the bulk bar (Firm, Convert and Cancel on All, Accept for
// `reschedule_out` on Action messages) with its per-id result panel, and the
// explanation drawer opened from `?order=<id>`. Read on
// `planning.planned_order.read`; Firm, Cancel and Accept on
// `planning.planned_order.manage`, and Convert (P3-H-3, §3.4) on
// `planning.planned_order.convert`, which procurement holds for purchase
// orders alone, so a procurement reader may select firmed rows to convert and
// nothing else.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { EmptyState, ErrorState } from "../../../../components/states";
import { Tabs } from "../../../../components/tabs";
import { Banner } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import {
  ActionMessageChip,
  ConvertedDocument,
  ExactQty,
  PlannedOrderKindChip,
  PlannedOrderStatusChip,
} from "../components/mrp-bits";
import {
  LateChip,
  PlannedOrderDrawer,
  ProductName,
  refusalText,
  suggestionText,
} from "../components/planned-order-drawer";
import {
  ACTION_MESSAGE_KINDS,
  PLANNED_ORDER_BULK_MAX,
  PLANNED_ORDER_KINDS,
  PLANNED_ORDER_REASON_MAX,
  PLANNED_ORDER_STATUSES,
  bulkAcceptActionMessages,
  cancelPlannedOrder,
  convertPlannedOrders,
  firmPlannedOrders,
  readPlannedOrderIds,
  useMrpRefresh,
  useMrpRun,
  usePlannedOrders,
  type PlannedOrder,
} from "../hooks/mrp-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { useProductIndex, useUoms } from "../hooks/planning-queries";
import {
  activeFilters,
  bulkAcceptable,
  cancelFanOut,
  compactSearch,
  convertOutcomesOf,
  convertSelection,
  countByKind,
  filtersOf,
  outcomesOf,
  validatePlannedOrdersSearch,
  type BulkOutcome,
  type PlannedOrdersSearch,
} from "../lib/mrp-lib";
import { formatDate, useOffline } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { shortId } from "../../../../lib/short-id";

type Dialog = "firm" | "convert" | "cancel" | "accept";

export function PlanningPlannedOrdersScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canManage = perms.has("planning.planned_order.manage");
  const canConvert = perms.has("planning.planned_order.convert");
  const desktop = useDesktop();
  const offline = useOffline();
  const refresh = useMrpRefresh();

  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validatePlannedOrdersSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );
  const view = search.view ?? "all";
  const filters = useMemo(() => filtersOf(search), [search]);
  const setSearch = (next: PlannedOrdersSearch) => {
    void navigate({ to: "/planning/orders", search: next, replace: true });
  };
  const patch = (changes: Partial<PlannedOrdersSearch>) => {
    setSearch(compactSearch({ ...search, ...changes }));
  };

  // Cursor paging; a filter, tab or facility change starts again from page one.
  const pageKey = `${facility.id}|${JSON.stringify(filters)}`;
  const [paging, setPaging] = useState<{ key: string; cursors: (string | undefined)[] }>({
    key: pageKey,
    cursors: [undefined],
  });
  if (paging.key !== pageKey) setPaging({ key: pageKey, cursors: [undefined] });
  const cursors = paging.key === pageKey ? paging.cursors : [undefined];
  const page = cursors.length - 1;
  const list = usePlannedOrders(facility.id, filters, cursors[page]);
  const rows = useMemo(() => list.data?.items ?? [], [list.data]);

  // The selection, keyed by id, with the rows it was made from so a selection
  // that reaches past the loaded page still knows each order's kind and status.
  const [selected, setSelected] = useState<ReadonlyMap<string, PlannedOrder>>(new Map());
  const [moreThanMax, setMoreThanMax] = useState(false);
  const [dialog, setDialog] = useState<Dialog | undefined>(undefined);
  const [results, setResults] = useState<{ verb: Dialog; outcomes: BulkOutcome[] } | undefined>();
  const [selecting, setSelecting] = useState(false);
  const [selectFailure, setSelectFailure] = useState<unknown>(undefined);
  useEffect(() => {
    setSelected(new Map());
    setMoreThanMax(false);
  }, [pageKey]);

  const authError = list.error instanceof UnauthorizedError ? list.error : undefined;
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const products = useProductIndex(rows.map((r) => r.product_id));
  const uoms = useUoms();
  const unitOf = (uomId: string) => uoms.data?.items.find((u) => u.id === uomId)?.code;
  const runFilter = useMrpRun(search.run);

  // Accept is a manage write, so the Action messages tab selects only for a
  // manager; on All a reader who may only convert selects firmed rows alone.
  const selectable = desktop && (view === "messages" ? canManage : canManage || canConvert);
  const rowSelectable = (row: PlannedOrder) =>
    view === "messages"
      ? row.status === "firmed" && bulkAcceptable(row.action_message)
      : canManage
        ? row.status === "proposed" || row.status === "firmed"
        : row.status === "firmed";

  const columns: DataTableColumn<PlannedOrder>[] = [
    {
      key: "po_no",
      header: t("planning.orders.columns.poNo"),
      id: true,
      render: (row) => <span data-testid="planning-order-po-no">{row.po_no}</span>,
    },
    {
      key: "kind",
      header: t("planning.orders.columns.kind"),
      render: (row) => <PlannedOrderKindChip kind={row.kind} />,
    },
    {
      key: "product",
      header: t("planning.orders.columns.product"),
      render: (row) => (
        <ProductName product={products.byId.get(row.product_id)} id={row.product_id} />
      ),
    },
    {
      key: "qty",
      header: t("planning.orders.columns.qty"),
      numeric: true,
      render: (row) => <ExactQty qty={row.qty} unit={unitOf(row.uom_id)} />,
    },
    {
      key: "start",
      header: t("planning.orders.columns.start"),
      render: (row) => formatDate(row.start_date, i18n.language),
    },
    {
      key: "due",
      header: t("planning.orders.columns.due"),
      render: (row) => (
        <span className="planning-chips" data-date={row.due_date}>
          <span>{formatDate(row.due_date, i18n.language)}</span>
          {row.late ? <LateChip /> : null}
        </span>
      ),
    },
    {
      key: "status",
      header: t("planning.orders.columns.status"),
      render: (row) => <PlannedOrderStatusChip status={row.status} />,
    },
    {
      key: "flags",
      header: t("planning.orders.columns.flags"),
      render: (row) => (
        <span className="planning-chips">
          {row.action_message !== null ? <ActionMessageChip kind={row.action_message} /> : null}
          {row.exceeds_mps ? (
            <StatusChip variant="warn" dot={false} data-testid="planning-order-exceeds-mps">
              {t("planning.orders.exceedsMps")}
            </StatusChip>
          ) : null}
          {row.stale_pin ? (
            <StatusChip variant="warn" dot={false} data-testid="planning-order-stale-pin">
              {t("planning.orders.stalePin")}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      // §4.5: the converted document as a link (P3-H-3).
      key: "document",
      header: t("planning.orders.columns.document"),
      render: (row) =>
        row.converted_kind !== null && row.converted_ref_id !== null ? (
          <ConvertedDocument kind={row.converted_kind} id={row.converted_ref_id} />
        ) : (
          ""
        ),
    },
  ];
  if (view === "messages") {
    columns.push({
      key: "suggestion",
      header: t("planning.orders.columns.suggestion"),
      render: (row) =>
        row.action_message_expected === null
          ? ""
          : suggestionText(t, i18n.language, row.action_message_expected),
    });
  }

  const openOrder = (id: string) => {
    void navigate({ to: "/planning/orders", search: { ...search, order: id } });
  };
  const closeOrder = () => {
    void navigate({
      to: "/planning/orders",
      search: compactSearch({ ...search, order: undefined }),
      replace: true,
    });
  };

  const selectedRows = [...selected.values()];
  const counts = countByKind(selectedRows);

  const selectAllMatching = async () => {
    setSelecting(true);
    setSelectFailure(undefined);
    try {
      const all = await readPlannedOrderIds(facility.id, filters, PLANNED_ORDER_BULK_MAX);
      setSelected(new Map(all.rows.filter(rowSelectable).map((r) => [r.id, r])));
      setMoreThanMax(all.more);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      setSelectFailure(error);
    } finally {
      setSelecting(false);
    }
  };

  const filtered = activeFilters(search);
  const header = <PlanningHeader title={t("planning.orders.title")} />;

  if (list.error !== null && authError === undefined && list.data === undefined) {
    return (
      <section className="planning-screen" data-testid="planning-orders">
        {header}
        <ErrorState
          title={t("planning.orders.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const emptyState =
    filtered.length > 0 || view === "messages" ? (
      <EmptyState
        title={
          view === "messages" && filtered.length === 0
            ? t("planning.orders.noMessagesTitle")
            : t("planning.orders.filteredEmptyTitle")
        }
        body={
          filtered.length === 0
            ? t("planning.orders.noMessagesBody")
            : t("planning.orders.filteredEmptyBody", {
                filters: filtered.map((k) => t(`planning.orders.filters.${k}`)).join(", "),
              })
        }
        {...(filtered.length > 0
          ? {
              ctaLabel: t("planning.common.clearFilters"),
              onCta: () => {
                setSearch(view === "messages" ? { view } : {});
              },
            }
          : {})}
      />
    ) : (
      <EmptyState title={t("planning.orders.emptyTitle")} body={t("planning.orders.emptyBody")} />
    );

  return (
    <section className="planning-screen" data-testid="planning-orders" data-view={view}>
      {header}
      {list.data?.purchase_only === true ? (
        <p className="planning-note" data-testid="planning-orders-purchase-only">
          {t("planning.orders.purchaseOnly")}
        </p>
      ) : null}
      <Tabs
        tabs={[
          {
            id: "all",
            label: t("planning.orders.tabs.all"),
            "data-testid": "planning-orders-tab-all",
          },
          {
            id: "messages",
            label: t("planning.orders.tabs.messages"),
            "data-testid": "planning-orders-tab-messages",
          },
        ]}
        active={view}
        onSelect={(id) => {
          setSearch(
            compactSearch({
              ...search,
              view: id === "messages" ? "messages" : undefined,
              message: id === "messages" ? search.message : undefined,
            }),
          );
        }}
        label={t("planning.orders.tabs.label")}
        data-testid="planning-orders-tabs"
      />
      <FilterBar search={search} view={view} onPatch={patch} runNo={runFilter.data?.run_no} />
      {offline && canManage ? (
        <p className="planning-note">{t("planning.common.offlineWrites")}</p>
      ) : null}
      {selectable ? (
        <div className="planning-actions planning-orders-select">
          <Button
            size="sm"
            busy={selecting}
            busyLabel={t("planning.orders.selecting")}
            data-testid="planning-orders-select-matching"
            onClick={() => void selectAllMatching()}
          >
            {t("planning.orders.selectMatching", { max: PLANNED_ORDER_BULK_MAX })}
          </Button>
        </div>
      ) : null}
      {moreThanMax ? (
        <Banner kind="warn" data-testid="planning-orders-over-max">
          {t("planning.orders.overMax", { max: PLANNED_ORDER_BULK_MAX })}
        </Banner>
      ) : null}
      {selectFailure !== undefined ? (
        <Banner kind="bad" data-testid="planning-orders-select-failed">
          {t("planning.common.saveFailed", { requestId: requestIdOf(selectFailure) ?? "-" })}
        </Banner>
      ) : null}
      {results !== undefined ? (
        <ResultPanel
          verb={results.verb}
          outcomes={results.outcomes}
          onOpen={openOrder}
          onClose={() => {
            setResults(undefined);
          }}
        />
      ) : null}
      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          rowTestId="planning-order-row"
          rowProps={(r) => ({
            "data-order-id": r.id,
            "data-po-no": r.po_no,
            "data-status": r.status,
            "data-kind": r.kind,
          })}
          onRowClick={(r) => {
            openOrder(r.id);
          }}
          loading={list.isPending}
          loadingLabel={t("planning.orders.loading")}
          caption={t("planning.orders.title")}
          fit
          empty={emptyState}
          error={
            list.error !== null && authError === undefined ? (
              <ErrorState
                title={t("planning.orders.loadError")}
                retryLabel={t("planning.common.retry")}
                onRetry={() => void list.refetch()}
                requestId={requestIdOf(list.error)}
                requestIdLabel={t("planning.common.requestId")}
              />
            ) : undefined
          }
          {...(selectable
            ? {
                selectable: {
                  selected: new Set(selected.keys()),
                  onChange: (next: ReadonlySet<string>) => {
                    const map = new Map<string, PlannedOrder>();
                    for (const id of next) {
                      const row = rows.find((r) => r.id === id) ?? selected.get(id);
                      if (row !== undefined) map.set(id, row);
                    }
                    setSelected(map);
                    setMoreThanMax(false);
                  },
                  headerLabel: t("planning.orders.selectPage"),
                  rowLabel: (r: PlannedOrder) =>
                    t("planning.orders.selectRow", { number: r.po_no }),
                  isRowSelectable: rowSelectable,
                  testId: "planning-order-select",
                },
                bulkBar: {
                  countLabel: t("planning.orders.selectedCount", { count: selected.size }),
                  clearLabel: t("planning.orders.clearSelection"),
                  onClear: () => {
                    setSelected(new Map());
                    setMoreThanMax(false);
                  },
                  children:
                    view === "messages" ? (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={offline}
                        data-testid="planning-orders-bulk-accept"
                        onClick={() => {
                          setDialog("accept");
                        }}
                      >
                        {t("planning.orders.accept")}
                      </Button>
                    ) : (
                      <>
                        {canManage ? (
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={offline}
                            data-testid="planning-orders-bulk-firm"
                            onClick={() => {
                              setDialog("firm");
                            }}
                          >
                            {t("planning.orders.firm")}
                          </Button>
                        ) : null}
                        {canConvert ? (
                          <Button
                            size="sm"
                            variant={canManage ? "secondary" : "primary"}
                            disabled={offline}
                            data-testid="planning-orders-bulk-convert"
                            onClick={() => {
                              setDialog("convert");
                            }}
                          >
                            {t("planning.orders.convert")}
                          </Button>
                        ) : null}
                        {canManage ? (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={offline}
                            data-testid="planning-orders-bulk-cancel"
                            onClick={() => {
                              setDialog("cancel");
                            }}
                          >
                            {t("planning.orders.cancelOrders")}
                          </Button>
                        ) : null}
                      </>
                    ),
                },
              }
            : {})}
          pager={{
            range: t("planning.common.page", { page: page + 1 }),
            page: page + 1,
            onPage: (n) => {
              if (n > page + 1 && list.data?.next_cursor) {
                const next = list.data.next_cursor;
                setPaging({ key: pageKey, cursors: [...cursors, next] });
              } else if (n < page + 1) setPaging({ key: pageKey, cursors: cursors.slice(0, -1) });
            },
            prevLabel: t("planning.common.previous"),
            nextLabel: t("planning.common.next"),
            hasPrev: page > 0,
            hasNext: (list.data?.next_cursor ?? null) !== null,
          }}
        />
      </Card>
      {dialog !== undefined ? (
        <BulkDialog
          verb={dialog}
          rows={selectedRows}
          counts={counts}
          onClose={() => {
            setDialog(undefined);
          }}
          onDone={(outcomes) => {
            setDialog(undefined);
            setResults({ verb: dialog, outcomes });
            setSelected(new Map());
            setMoreThanMax(false);
            void refresh(
              facility.id,
              outcomes.map((o) => o.id),
            );
          }}
        />
      ) : null}
      {search.order !== undefined ? (
        <PlannedOrderDrawer
          key={search.order}
          id={search.order}
          onClose={closeOrder}
          onOpen={openOrder}
        />
      ) : null}
    </section>
  );
}

function FilterBar({
  search,
  view,
  onPatch,
  runNo,
}: {
  search: PlannedOrdersSearch;
  view: "all" | "messages";
  onPatch: (changes: Partial<PlannedOrdersSearch>) => void;
  runNo: string | undefined;
}) {
  const { t } = useTranslation();
  const [planner, setPlanner] = useState(search.planner ?? "");
  useEffect(() => {
    setPlanner(search.planner ?? "");
  }, [search.planner]);
  const chip = (key: keyof PlannedOrdersSearch, label: ReactNode) => (
    <span className="planning-filter-tag" data-testid="planning-orders-filter-tag" data-key={key}>
      {label}
      <Button
        size="sm"
        aria-label={t("planning.orders.removeFilter", {
          filter: t(`planning.orders.filters.${key}`),
        })}
        onClick={() => {
          onPatch({ [key]: undefined });
        }}
      >
        ✕
      </Button>
    </span>
  );
  return (
    <div className="filter-bar planning-filters" data-testid="planning-orders-filters">
      <Field label={t("planning.orders.filters.kind")}>
        <Select
          value={search.kind ?? ""}
          data-testid="planning-orders-filter-kind"
          onChange={(e) => {
            onPatch({ kind: (e.target.value || undefined) as PlannedOrdersSearch["kind"] });
          }}
        >
          <option value="">{t("planning.common.all")}</option>
          {PLANNED_ORDER_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`planning.orders.kind.${k}`)}
            </option>
          ))}
        </Select>
      </Field>
      {view === "all" ? (
        <Field label={t("planning.orders.filters.status")}>
          <Select
            value={search.status ?? ""}
            data-testid="planning-orders-filter-status"
            onChange={(e) => {
              onPatch({ status: (e.target.value || undefined) as PlannedOrdersSearch["status"] });
            }}
          >
            <option value="">{t("planning.common.all")}</option>
            {PLANNED_ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`planning.orders.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <Field label={t("planning.orders.filters.message")}>
        <Select
          value={search.message ?? ""}
          data-testid="planning-orders-filter-message"
          onChange={(e) => {
            onPatch({ message: (e.target.value || undefined) as PlannedOrdersSearch["message"] });
          }}
        >
          <option value="">{t("planning.common.all")}</option>
          {view === "all" ? (
            <>
              <option value="true">{t("planning.orders.messageAny")}</option>
              <option value="false">{t("planning.orders.messageNone")}</option>
            </>
          ) : null}
          {ACTION_MESSAGE_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`planning.orders.message.${k}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("planning.orders.filters.from")}>
        <input
          type="date"
          value={search.from ?? ""}
          data-testid="planning-orders-filter-from"
          onChange={(e) => {
            onPatch({ from: e.target.value || undefined });
          }}
        />
      </Field>
      <Field label={t("planning.orders.filters.to")}>
        <input
          type="date"
          value={search.to ?? ""}
          data-testid="planning-orders-filter-to"
          onChange={(e) => {
            onPatch({ to: e.target.value || undefined });
          }}
        />
      </Field>
      <Field label={t("planning.orders.filters.planner")}>
        <input
          type="search"
          value={planner}
          maxLength={64}
          data-testid="planning-orders-filter-planner"
          onChange={(e) => {
            setPlanner(e.target.value);
          }}
          onBlur={() => {
            onPatch({ planner: planner.trim() || undefined });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") onPatch({ planner: planner.trim() || undefined });
          }}
        />
      </Field>
      {search.run !== undefined
        ? chip("run", t("planning.orders.filterTag.run", { run: runNo ?? shortId(search.run) }))
        : null}
      {search.product !== undefined
        ? chip("product", t("planning.orders.filterTag.product"))
        : null}
      {search.line !== undefined ? chip("line", t("planning.orders.filterTag.line")) : null}
      {search.entry !== undefined ? chip("entry", t("planning.orders.filterTag.entry")) : null}
    </div>
  );
}

function BulkDialog({
  verb,
  rows,
  counts,
  onClose,
  onDone,
}: {
  verb: Dialog;
  rows: readonly PlannedOrder[];
  counts: Record<"manufacture" | "purchase" | "transfer", number>;
  onClose: () => void;
  onDone: (outcomes: BulkOutcome[]) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);
  const trimmed = reason.trim();
  const poNoOf = (id: string) => rows.find((r) => r.id === id)?.po_no ?? shortId(id);
  const notFirmed = rows.filter((r) => r.status !== "firmed").length;
  // Buy orders are sent and answered 422 PURCHASE_CONVERSION_UNAVAILABLE, a
  // stated absence until module 10; the dialog says so before the press.
  const firmedBuy = rows.filter((r) => r.status === "firmed" && r.kind === "purchase").length;

  const send = async () => {
    setSending(true);
    setFailure(undefined);
    try {
      if (verb === "firm") {
        const res = await firmPlannedOrders(rows.map((r) => r.id));
        onDone(outcomesOf(res.results, poNoOf));
      } else if (verb === "convert") {
        const split = convertSelection(rows);
        if (split.send.length === 0) {
          onDone(split.notSent);
        } else {
          const res = await convertPlannedOrders(split.send);
          onDone(convertOutcomesOf(res.results, split.notSent, poNoOf));
        }
      } else if (verb === "accept") {
        const items = rows.flatMap((r) =>
          r.action_message_expected === null
            ? []
            : [{ id: r.id, expected_message: r.action_message_expected }],
        );
        const res = await bulkAcceptActionMessages(items);
        onDone(outcomesOf(res.results, poNoOf));
      } else {
        onDone(
          await cancelFanOut(rows, async (id) => {
            try {
              await cancelPlannedOrder(id, trimmed);
              return undefined;
            } catch (error) {
              if (error instanceof UnauthorizedError) throw error;
              if (error instanceof RequestError) {
                const status = (
                  error.problem as { order_status?: PlannedOrder["status"] } | undefined
                )?.order_status;
                return {
                  ...(error.code !== undefined ? { code: error.code } : {}),
                  status: error.httpStatus,
                  ...(status !== undefined ? { orderStatus: status } : {}),
                };
              }
              return { code: "NETWORK", status: 0 };
            }
          }),
        );
      }
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      setFailure(error);
    } finally {
      setSending(false);
    }
  };

  const title = t(`planning.orders.bulk.${verb}Title`);
  const kinds = PLANNED_ORDER_KINDS.filter((k) => counts[k] > 0)
    .map((k) => t(`planning.orders.bulk.kindCount.${k}`, { count: counts[k] }))
    .join(", ");
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={sending}
      className="planning-dialog"
      testId={`planning-orders-${verb}-dialog`}
    >
      <div className="planning-mps-decision">
        <p data-testid="planning-orders-bulk-summary">
          {t(`planning.orders.bulk.${verb}Body`, { count: rows.length, kinds })}
        </p>
        {verb === "cancel" && notFirmed > 0 ? (
          <p className="planning-note" data-testid="planning-orders-bulk-not-firmed">
            {t("planning.orders.bulk.notFirmed", { count: notFirmed })}
          </p>
        ) : null}
        {verb === "convert" && notFirmed > 0 ? (
          <p className="planning-note" data-testid="planning-orders-bulk-not-firmed">
            {t("planning.orders.bulk.convertNotFirmed", { count: notFirmed })}
          </p>
        ) : null}
        {verb === "convert" && firmedBuy > 0 ? (
          <p className="planning-note" data-testid="planning-orders-bulk-buy">
            {t("planning.orders.bulk.convertBuy", { count: firmedBuy })}
          </p>
        ) : null}
        {verb === "cancel" ? (
          <Field label={t("planning.orders.cancelReason")} wide>
            <textarea
              rows={3}
              maxLength={PLANNED_ORDER_REASON_MAX}
              value={reason}
              data-testid="planning-orders-cancel-reason"
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Field>
        ) : null}
        {failure !== undefined ? (
          <Banner kind="bad" data-testid="planning-orders-bulk-failed">
            {t("planning.common.saveFailed", { requestId: requestIdOf(failure) ?? "-" })}
          </Banner>
        ) : null}
        <div className="planning-mps-decision-actions">
          <Button disabled={sending} onClick={onClose}>
            {t("planning.common.cancel")}
          </Button>
          <Button
            variant={verb === "cancel" ? "danger" : "primary"}
            busy={sending}
            busyLabel={t("planning.common.saving")}
            disabled={offline || rows.length === 0 || (verb === "cancel" && trimmed.length === 0)}
            data-testid={`planning-orders-${verb}-confirm`}
            onClick={() => void send()}
          >
            {t(`planning.orders.bulk.${verb}Confirm`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ResultPanel({
  verb,
  outcomes,
  onOpen,
  onClose,
}: {
  verb: Dialog;
  outcomes: readonly BulkOutcome[];
  onOpen: (id: string) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const done = outcomes.filter((o) => o.result !== null).length;
  return (
    <Card
      title={t(`planning.orders.result.${verb}Title`)}
      action={
        <Button size="sm" onClick={onClose} data-testid="planning-orders-result-close">
          {t("planning.common.close")}
        </Button>
      }
      data-testid="planning-orders-result"
    >
      <p className="planning-note" data-testid="planning-orders-result-summary">
        {t("planning.orders.result.summary", { done, refused: outcomes.length - done })}
      </p>
      <ul className="planning-result-list">
        {outcomes.map((o) => (
          <li
            key={o.id}
            data-testid="planning-orders-result-row"
            data-order-id={o.id}
            data-result={o.result ?? "refused"}
            data-code={o.code}
          >
            <button
              type="button"
              className="planning-linklike"
              onClick={() => {
                onOpen(o.id);
              }}
            >
              {o.poNo}
            </button>
            {o.result !== null ? (
              <span className="planning-chips">
                <StatusChip variant="ok">
                  {o.alreadyConverted === true
                    ? t("planning.orders.result.alreadyConverted")
                    : t(`planning.orders.result.${o.result}`)}
                </StatusChip>
                {o.convertedKind !== undefined && o.convertedRefId !== undefined ? (
                  <ConvertedDocument
                    kind={o.convertedKind}
                    id={o.convertedRefId}
                    testId="planning-orders-result-document"
                  />
                ) : null}
              </span>
            ) : (
              <span className="planning-bad">
                {o.notSent === true
                  ? t("planning.orders.result.notSent")
                  : refusalText(t, o.code ?? "other")}
                {o.pinReason !== undefined
                  ? ` ${t(`planning.orders.pin.${o.pinReason}`)}`
                  : o.code === "PLANNED_ORDER_NO_ROUTING" && o.startDate !== undefined
                    ? ` ${t("planning.orders.noRoutingOn", {
                        date: formatDate(o.startDate, i18n.language),
                      })}`
                    : ""}
                {o.currentMessage !== undefined && o.currentMessage !== null
                  ? ` ${t("planning.orders.drawer.messageNow", {
                      message: suggestionText(t, i18n.language, o.currentMessage),
                    })}`
                  : ""}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
